import { AiError } from './types'
import { fetchOrThrow } from './network'
import type { AiSettings, TtsProvider } from '@/types/settings'
import { track } from '@/composables/useEventTracker'

/**
 * Síntesis de voz neural para el modo audiolibro.
 *
 * Dos proveedores, elegidos con `settings.ai.ttsProvider`:
 *
 *   - **OpenAI**, que sigue el enrutado del resto de la IA
 *     (`settings.ai.routing`): 'direct' llama a OpenAI con la key del
 *     usuario en el navegador; 'proxy' llama a `{proxyEndpoint}/tts` y la
 *     key vive en el Worker. Exige tener OpenAI como proveedor activo,
 *     porque comparte con él key y baseUrl.
 *   - **ElevenLabs**, con su propia key (`ai.elevenLabsKey`) y SIEMPRE
 *     directo: el Worker solo enruta OpenAI. A cambio, no depende del
 *     proveedor activo — con Anthropic u Ollama para el chat, la voz
 *     neural sigue disponible.
 *
 * Si la config no da para sintetizar, `ttsSupported()` devuelve false y la
 * UI muestra el motivo en vez de fallar en silencio.
 */

/** Voces de la API de OpenAI. No hay endpoint que las liste. */
export const TTS_VOICES = [
  'alloy', 'ash', 'ballad', 'coral', 'echo',
  'fable', 'nova', 'onyx', 'sage', 'shimmer',
] as const

export const TTS_MODEL = 'gpt-4o-mini-tts'

/** Límite de caracteres por llamada que impone la API. */
export const TTS_MAX_CHARS = 4096

/** Base de la API de ElevenLabs (no es configurable: no hay self-hosted). */
const ELEVENLABS_BASE = 'https://api.elevenlabs.io/v1'

/** Modelo de ElevenLabs. El multilingüe lee español con cualquier voz. */
export const ELEVENLABS_MODEL = 'eleven_multilingual_v2'

/** Límite de caracteres por llamada de `eleven_multilingual_v2`. */
export const ELEVENLABS_MAX_CHARS = 5000

/**
 * Precio en USD por millón de caracteres sintetizados, por proveedor.
 *
 * ⚠️ VERIFICAR contra las tarifas vigentes antes de dar por bueno el coste
 * que se muestra al usuario. Una cifra obsoleta aquí es una promesa falsa en
 * la UI. Son las únicas constantes de precio del módulo a propósito: se
 * actualizan en un solo sitio.
 *
 * ElevenLabs cobra por créditos (≈1 crédito por carácter) y el precio por
 * crédito baja con el plan, así que su cifra es la del plan de entrada
 * (Creator): el coste real de un plan superior será MENOR que el estimado,
 * nunca mayor. Sobrestimar es el lado seguro para una cifra que el usuario
 * usa para decidir si pulsa play.
 */
export const TTS_USD_PER_MILLION_CHARS = 12
export const ELEVENLABS_USD_PER_MILLION_CHARS = 220

/** Coste estimado en USD de sintetizar `chars` caracteres. */
export function estimateTtsCostUsd(chars: number, provider: TtsProvider = 'openai'): number {
  const rate =
    provider === 'elevenlabs' ? ELEVENLABS_USD_PER_MILLION_CHARS : TTS_USD_PER_MILLION_CHARS
  return (chars / 1_000_000) * rate
}

/** Caracteres que admite una sola llamada con la config dada. */
export function ttsMaxChars(ai: AiSettings): number {
  return ai.ttsProvider === 'elevenlabs' ? ELEVENLABS_MAX_CHARS : TTS_MAX_CHARS
}

/** ¿Puede la config actual sintetizar voz neural? */
export function ttsSupported(ai: AiSettings): boolean {
  // ElevenLabs solo necesita su key: ni proveedor activo ni proxy pintan nada.
  if (ai.ttsProvider === 'elevenlabs') return (ai.elevenLabsKey ?? '').trim().length > 0
  if (ai.activeProvider !== 'openai') return false
  if (ai.routing === 'proxy') return ai.proxyEndpoint.trim().length > 0
  return true
}

/** Una voz de la cuenta de ElevenLabs. */
export interface ElevenLabsVoice {
  id: string
  name: string
}

/**
 * Voces disponibles en la cuenta de ElevenLabs (las de su biblioteca por
 * defecto más las clonadas por el usuario).
 *
 * No hay lista que codificar en el cliente: los IDs son por cuenta y el
 * catálogo cambia. Por eso esta es la única forma honesta de poblar el
 * selector de voz, y de ahí una petición extra.
 */
export async function listElevenLabsVoices(
  ai: AiSettings,
  fetchImpl: typeof fetch = fetch,
): Promise<ElevenLabsVoice[]> {
  const key = (ai.elevenLabsKey ?? '').trim()
  if (!key) return []
  const resp = await fetchOrThrow(
    `${ELEVENLABS_BASE}/voices`,
    { headers: { 'xi-api-key': key } },
    'ElevenLabs voces',
    fetchImpl,
  )
  if (!resp.ok) {
    throw new AiError(`ElevenLabs: ${resp.status}`, resp.status)
  }
  const data = (await resp.json()) as { voices?: { voice_id?: string; name?: string }[] }
  return (data.voices ?? [])
    .filter((v): v is { voice_id: string; name?: string } => Boolean(v.voice_id))
    .map((v) => ({ id: v.voice_id, name: v.name || v.voice_id }))
}

/** Base del proxy sin barra final (evita `//tts` → 404 en el Worker). */
function proxyBase(endpoint: string): string {
  return endpoint.replace(/\/+$/, '')
}

/**
 * Sintetiza `text` con la voz `voice` y devuelve el audio como Blob (mp3).
 *
 * `fetchImpl` existe para los tests; en producción se usa el fetch global.
 */
export async function synthesize(
  text: string,
  voice: string,
  ai: AiSettings,
  fetchImpl: typeof fetch = fetch,
): Promise<Blob> {
  if (!ttsSupported(ai)) {
    throw new AiError(
      ai.ttsProvider === 'elevenlabs'
        ? 'Falta la API key de ElevenLabs. Añádela en Ajustes → IA.'
        : 'La voz neural requiere OpenAI. Cámbialo en Ajustes → IA.',
    )
  }
  const input = text.trim()
  if (!input) {
    throw new AiError('No hay texto que sintetizar.')
  }
  const limit = ttsMaxChars(ai)
  if (input.length > limit) {
    throw new AiError(
      `El fragmento excede el límite de ${limit} caracteres de la API (${input.length}).`,
    )
  }

  const blob =
    ai.ttsProvider === 'elevenlabs'
      ? await elevenLabsSpeech(input, voice, ai, fetchImpl)
      : await openAiSpeech(input, voice, ai, fetchImpl)

  // Evento de uso (caracteres, no tokens). Sin contenido del libro.
  void track('tts_request', {
    chars: input.length,
    voice,
    routing: ai.ttsProvider === 'elevenlabs' ? 'direct' : ai.routing,
    provider: ai.ttsProvider ?? 'openai',
  })

  return blob
}

/** Ruta OpenAI: directa o por el Worker, según `ai.routing`. */
async function openAiSpeech(
  input: string,
  voice: string,
  ai: AiSettings,
  fetchImpl: typeof fetch,
): Promise<Blob> {
  const isProxy = ai.routing === 'proxy'
  const url = isProxy
    ? `${proxyBase(ai.proxyEndpoint)}/tts`
    : `${ai.providers.openai.baseUrl.replace(/\/+$/, '')}/audio/speech`

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (isProxy) {
    if (ai.proxyToken) headers['x-app-token'] = ai.proxyToken
  } else {
    headers.Authorization = `Bearer ${ai.providers.openai.apiKey}`
  }

  const resp = await fetchOrThrow(
    url,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model: TTS_MODEL,
        voice,
        input,
        response_format: 'mp3',
      }),
    },
    isProxy ? 'Proxy TTS' : 'OpenAI TTS',
    fetchImpl,
  )

  if (!resp.ok) {
    const detail = await resp.text().catch(() => '')
    throw new AiError(`TTS: ${resp.status} ${detail}`.trim(), resp.status)
  }

  return await resp.blob()
}

/**
 * Ruta ElevenLabs: siempre directa, con la key en la cabecera `xi-api-key`.
 *
 * ponytail: sin `voice_settings` (estabilidad, similitud, estilo). Los
 * valores por defecto de la voz son razonables y la velocidad ya la pone
 * `audio.playbackRate`, que es gratis y no obliga a re-sintetizar. Se añaden
 * si alguien los echa de menos.
 */
async function elevenLabsSpeech(
  input: string,
  voice: string,
  ai: AiSettings,
  fetchImpl: typeof fetch,
): Promise<Blob> {
  if (!voice.trim()) {
    throw new AiError('Elige una voz de ElevenLabs en el reproductor.')
  }
  const resp = await fetchOrThrow(
    `${ELEVENLABS_BASE}/text-to-speech/${encodeURIComponent(voice)}?output_format=mp3_44100_128`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'xi-api-key': (ai.elevenLabsKey ?? '').trim(),
      },
      body: JSON.stringify({ text: input, model_id: ELEVENLABS_MODEL }),
    },
    'ElevenLabs TTS',
    fetchImpl,
  )

  if (!resp.ok) {
    const detail = await resp.text().catch(() => '')
    throw new AiError(`TTS: ${resp.status} ${detail}`.trim(), resp.status)
  }

  // ElevenLabs responde `application/octet-stream`, y ese tipo llega tal cual
  // al Blob, al caché y al ObjectURL — donde un `<audio>` puede negarse a
  // reproducirlo. Es mp3: se reetiqueta como tal.
  return new Blob([await resp.arrayBuffer()], { type: 'audio/mpeg' })
}
